import {
  prisma,
  type Prisma,
  type LineItem,
  type OrderStatus as OrderStatusType,
  OrderStatus,
  OrderType,
  Currency,
  OrderItemStatus,
  OrderItem,
  PaymentStatus,
  EntityStatus,
  type Payment,
} from "client";
import {
  createOrderTransition,
  createOrderItemTransition,
} from "services/Transitions";
import { getTicket } from "services/ticket";
import { getTicketQuantity } from "services/ticketQuantity";
import { getEvent } from "services/event";
import { isPayedEvent } from "services/eventDashboardMetrics";
import {
  createSubtotal,
  calculateTaxBySubtotal,
  dineroSubUnit,
} from "@sajilni/money/helpers";
import {
  invalidateCheckinRosterForOrder,
  warmCheckinRosterEntriesForOrder,
} from "services/checkInOut/warmCheckinCache";
import { updatePayment } from "services/payment";
import { releaseOrdersInventory } from "services/expireOrder";
import {
  flattenTicketWithBookableItem,
  getEffectiveBookableItem,
  type EntityWithRelations,
} from "services/bookableItem/helpers";

//GET
export const getOrderById = async (id: string) => {
  try {
    const order = await prisma.order.findUnique({
      where: {
        id,
      },
      include: {
        formResults: {
          include: { formResult: { include: { formTemplate: true } } },
        },
        orderItems: {
          include: {
            formResults: {
              include: { formResult: { include: { formTemplate: true } } },
            },
            // TODO: This is done for Backward compatibility
            ticket: {
              include: {
                bookableItem: {
                  include: { translations: true },
                },
              },
            },
            entity: true,
            DTCMOrderItem: true,
          },
        },
        payments: true,
        dtcmOrder: true,
      },
    });

    if (!order) return null;

    // TODO: This is done for Backward compatibility
    // Flatten tickets in orderItems
    return {
      ...order,
      orderItems: order.orderItems.map((item) => ({
        ...item,
        ticket: item.ticket
          ? flattenTicketWithBookableItem(item.ticket as any)
          : null,
      })),
    };
  } catch (error) {
    console.error("Error fetching order by ID:", error);
    throw error;
  }
};

export const getOrderWithTicketDataByOrderId = async (
  OrderId: string,
  lang?: string,
  include?: Prisma.OrderInclude
) => {
  if (!OrderId) {
    throw new Error("OrderId cannot be null");
  }
  try {
    const order = await prisma.order.findUnique({
      where: {
        id: OrderId,
      },
      include: {
        orderItems: {
          include: {
            formResults: {
              include: {
                formResult: {
                  include: {
                    formTemplate: true,
                  },
                },
              },
            },
            // TODO: This is done for Backward compatibility
            invitationAttendee: {
              include: {
                invitation: {
                  include: {
                    formEntity: {
                      include: {
                        formTemplate: true,
                      },
                    },
                    translations: lang
                      ? {
                        where: {
                          language: {
                            code: lang,
                          },
                        },
                      }
                      : true,
                  },
                },
              },
            },
            // TODO: This is done for Backward compatibility
            ticket: {
              include: {
                dtcmTicketPrice: true,
                category: {
                  include: {
                    dtcmSections: true,
                  },
                },
                bookableItem: {
                  include: {
                    formEntities: {
                      include: {
                        formTemplate: true,
                      },
                    },
                    translations: lang
                      ? {
                        where: {
                          language: {
                            code: lang,
                          },
                        },
                      }
                      : true,
                    designs: {
                      include: {
                        translations: true,
                      },
                    },
                  },
                },
              },
            },
            entity: {
              include: {
                entityTranslations: true,
                entityTier: {
                  include: {
                    bookableItem: { include: { translations: true } },
                  },
                },
                floorPlan: {
                  include: {
                    formEntities: {
                      include: {
                        formTemplate: true,
                      },
                    },
                  },
                },
              },
            },
          },
        },
        formResults: {
          include: {
            formResult: {
              include: {
                formTemplate: true,
              },
            },
          },
        },
        profile: true,
      },
    });

    if (!order) return null;

    // TODO: This is done for Backward compatibility
    // Flatten tickets and map invitation in orderItems
    return {
      ...order,
      orderItems: order.orderItems.map((item) => ({
        ...item,
        ticket: item.ticket
          ? flattenTicketWithBookableItem(item.ticket as any)
          : null,
        // Map invitation from invitationAttendee for backward compatibility
        invitation: item.invitationAttendee?.invitation || null,
        entity: item.entity,
      })),
    };
  } catch (error) {
    console.error("Error fetching order by ID:", error);
    throw error;
  }
};

export const getAllOrder = async () => {
  const order = await prisma.order.findMany();
  return order;
};

export const getOrderByNumber = async (
  orderNumber: number,
  eventId: string
) => {
  const [order, eventIsPaid] = await Promise.all([
    prisma.order.findUnique({
    where: {
      number_eventId: {
        number: Number(orderNumber),
        eventId,
      },
    },
    include: {
      profile: true,
      formResults: {
        include: { formResult: { include: { formTemplate: true } } },
      },
      event: {
        select: {
          timezone: true,
          taxRegion: {
            select: {
              saleTaxRate: true,
            },
          },
        },
      },
      payments: {
        select: {
          id: true,
          status: true,
          grandTotal: true,
          subTotal: true,
          totalTax: true,
          paymentFees: true,
          totalCharge: true,
          totalDiscountAmount: true,
          paymentMethodId: true,
          createdAt: true,
        },
      },
      appliedPricingAdjustments: {
        select: {
          id: true,
          target: true,
          pricingRuleId: true,
          orderItemId: true,
          paymentId: true,
          pricingRule: true,
        },
      },
      orderItems: {
        include: {
          formResults: {
            include: { formResult: { include: { formTemplate: true } } },
          },
          // TODO: This is done for Backward compatibility
          ticket: {
            include: {
              bookableItem: {
                include: {
                  formEntities: {
                    include: {
                      formTemplate: true,
                    },
                  },
                  translations: true,
                },
              },
            },
          },
          entity: {
            include: {
              entityTranslations: true,
              entityTier: {
                include: {
                  bookableItem: { include: { translations: true } },
                },
              },
            },
          },
          invitationAttendee: {
            include: {
              contact: true,
              invitation: {
                include: {
                  translations: true,
                },
              },
            },
          },
        },
      },
    },
  }),
    isPayedEvent(eventId),
  ]);

  return order
    ? {
      ...order,
      eventIsPaid,
      // TODO: This is done for Backward compatibility
      invitationAttendee: order?.orderItems.find(
        (item) => item.invitationAttendee
      )?.invitationAttendee,
      orderItems: order?.orderItems.map((item: any) => {
        return {
          ...item,
          // TODO: This is done for Backward compatibility
          ticket: item.ticket
            ? flattenTicketWithBookableItem(item.ticket as any)
            : null,
          formResult: item.formResults[0]?.formResult,
        };
      }),
    }
    : order;
};

export const generateOrderNumber = async (eventId?: string) => {
  if (!eventId) {
    throw new Error("Event ID is required");
  }

  const latestOrder = await prisma.order.findFirst({
    where: { eventId },
    orderBy: { number: "desc" },
    select: { number: true },
  });

  const lastNumber = latestOrder ? latestOrder.number : 0;
  return lastNumber + 1;
};

// POST
export const createOrder = async (
  selectedItems: any[],
  eventId: string,
  type: OrderType,
  withTicketsData?: boolean,
  attendee?: any,
  invitationId?: string,
  session?: any,
  lang?: string
) => {
  try {
    // Generate a new order number
    for (const item of selectedItems) {
      if (type === OrderType.ENTITY) {
        const { valid, error } = await checkEntityBoothAvailability(
          item.entityId
        );
        if (!valid) {
          throw new Error(error);
        }
      } else {
        const ticket = await getTicket(item.ticketId);
        if (!ticket) {
          throw new Error("Ticket not found");
        }
        const { valid, error } = isValidItemQuantity(
          item.quantity,
          ticket as any
        );
        if (!valid) {
          throw new Error(error);
        }
      }
    }

    const newNumber = await generateOrderNumber(eventId);
    let lineItems: any[] = [];
    // let event: Event | undefined;
    let subTotal = 0;
    let tax = 0;
    let total = 0;
    const event = await getEvent(eventId as string, "en", {
      eventMethodConfigs: {
        where: { disabled: false },
        take: 1,
      },
      taxRegion: true,
    });

    if (!event) throw new Error("This event not found");

    let subUnit: any = event?.currency
      ? dineroSubUnit({
        currency: event?.currency || Currency.SAR,
      })
      : 1;

    let taxRate = event?.taxRegion?.saleTaxRate
      ? event?.taxRegion?.saleTaxRate
      : 0;
    let currency: any = event?.currency ?? Currency.SAR;
    if (type !== OrderType.ENTITY) {
      for (const item of selectedItems) {
        const ticket = await getTicket(item?.ticketId);
        const itemTotal = item.quantity * (ticket?.price || 0);
        subTotal += createSubtotal({
          subTotal: itemTotal,
          subUnit: subUnit,
          currency: currency,
        });

        if (ticket?.taxable) {
          tax += calculateTaxBySubtotal({
            subTotal: itemTotal,
            subUnit: subUnit,
            taxRate: taxRate,
            currency: currency,
          });
        }
      }
    }

    total = subTotal + tax;

    if (!event?.eventMethodConfigs?.length && total !== 0)
      throw new Error("Please create and choose payment getaway");
    if (!event?.taxRegion && total !== 0)
      throw new Error("Please create and choose tax");

    // Create order items for selected items
    const orderItems = await createOrderItems(
      selectedItems,
      lineItems,
      subUnit,
      type,
      attendee?.id
    );

    // Calculate the expiry date based on event's orderExpiryDurationInMinutes
    const expiryDate = calculateExpiryDate(
      event?.orderExpiryDurationInMinutes || 60
    );
    let orderData: any = {};

    const order = await prisma.order.create({
      data: {
        number: Number(newNumber),
        status: OrderStatus.ON_GOING,
        orderItems: { create: orderItems },
        lineItems,
        expiryDate,
        subTotal,
        tax,
        total,
        taxRate,
        currency: currency || Currency.SAR,
        type,
        lang,
        event: {
          connect: { id: event?.id },
        },
      },
      include: {
        orderItems: {
          include: {
            // TODO: This is done for Backward compatibility
            invitationAttendee: {
              include: {
                invitation: {
                  include: {
                    formEntity: {
                      include: {
                        formTemplate: true,
                      },
                    },
                  },
                },
              },
            },
            // TODO: This is done for Backward compatibility
            ticket: {
              include: {
                bookableItem: {
                  include: {
                    formEntities: {
                      include: {
                        formTemplate: true,
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    });

    // Create transitions for order and order items
    const createdBy = session?.user?.name || "guest";
    const createdById = session?.user?.id || "guest";
    await createOrderTransition({
      status: order.status,
      orderId: order.id,
      createdBy,
      metaData: {
        createdBy,
        createdById,
      },
    });

    for (const orderItem of order.orderItems || []) {
      await createOrderItemTransition({
        status: orderItem.status,
        orderItemId: orderItem.id,
        createdBy,
        metaData: {
          createdBy,
          createdById,
        },
      });
    }

    // return order with tickets data
    if (withTicketsData) {
      const orderWithTicketData = await getOrderWithTicketDataByOrderId(
        order.id,
        lang
      );
      return orderWithTicketData;
    }

    // TODO: This is done for Backward compatibility
    // Flatten tickets and map invitation in orderItems
    return {
      ...order,
      orderItems: order.orderItems.map((item) => ({
        ...item,
        ticket: item.ticket ? flattenTicketWithBookableItem(item.ticket) : null,
        // Map invitation from invitationAttendee for backward compatibility
        invitation: item.invitationAttendee?.invitation || null,
      })),
    };
  } catch (error) {
    console.error("Error creating order:", error);
    throw error;
  }
};

export const checkItemQuantityValidity = (
  requestedQuantity: number,
  quantityValidation: QuantityValidationType,
  itemType: "ticket" | "entity"
) => {
  const { valid: isValidQuantity, error } = isValidItemQuantity(
    requestedQuantity,
    quantityValidation,
    itemType
  );

  if (!isValidQuantity) {
    throw new Error(error);
  }
};

// POST

const createOrderItems = async (
  selectedItems: any[],
  lineItems: any[],
  subUnit: number,
  type: OrderType,
  attendeeId?: string
) => {
  const orderItems: Prisma.OrderItemCreateWithoutOrderInput[] = [];
  for (const item of selectedItems) {
    if (type === OrderType.ENTITY) {
      const { valid, error } = await checkEntityBoothAvailability(
        item.entityId
      );
      if (!valid) {
        throw new Error(error);
      }
      orderItems.push({
        entity: { connect: { id: item.entityId } },
        status: OrderItemStatus.RESERVED,
        invitationAttendee: attendeeId
          ? { connect: { id: attendeeId } }
          : undefined,
      });
      // Reserve the entity
      await prisma.entity.update({
        where: { id: item.entityId },
        data: { status: EntityStatus.ON_HOLD },
      });

      // Fetch entity with pricing info based on configSource
      const entity = await prisma.entity.findUnique({
        where: { id: item.entityId },
        include: {
          bookableItem: true,
          entityTier: {
            include: {
              bookableItem: true,
            },
          },
        },
      });

      if (!entity) throw new Error("Entity not found");

      // Calculate entity price based on configSource
      const effectiveBookableItem = getEffectiveBookableItem(
        entity as EntityWithRelations
      );
      const entityPrice = effectiveBookableItem?.price ?? 0;

      lineItems.push({
        name: entity.label ?? "Unnamed Entity",
        description: entity.label ?? "No description",
        quantity: 1, // Assuming one entity per order item
        price: entityPrice * subUnit,
        image: "",
        id: entity.id,
      });
    } else {
      const ticket = await getTicket(item.ticketId);
      if (!ticket) throw new Error("Ticket not found");
      const { availableTicket } = (await getTicketQuantity(item.ticketId)) as {
        availableTicket: number | undefined;
      };
      // if (saleStatus !== "In progress" && item.published) {
      //   throw new Error("Ticket purchase is currently unavailable.");
      // }
      if (availableTicket && availableTicket >= item.quantity) {
        const itemTotal = item.quantity * (ticket?.price || 0);
        lineItems.push({
          name: ticket.translations?.[0].name,
          description: ticket.translations?.[0].description,
          quantity: item.quantity,
          price: itemTotal * (subUnit || 1),
          id: ticket.id,
          taxable: ticket?.taxable,
        });
        for (let i = 0; i < item.quantity; i++) {
          orderItems.push({
            ticket: { connect: { id: ticket.id } },
            invitationAttendee: attendeeId
              ? { connect: { id: attendeeId } }
              : undefined,
            ...(item.invitationId
              ? { invitation: { connect: { id: item.invitationId } } }
              : {}),
            status: ticket.requireApproval
              ? OrderItemStatus.PENDING_APPROVAL
              : OrderItemStatus.RESERVED,
          });
        }
      } else {
        throw new Error(
          "Not enough available tickets for the selected quantity"
        );
      }
    }
  }
  return orderItems;
};

const calculateExpiryDate = (expiryDuration: number) => {
  const expiryDate = new Date();
  expiryDate.setMinutes(expiryDate.getMinutes() + expiryDuration);
  return expiryDate;
};

const getPaymentsPaidTotal = (payments: Payment[]) =>
  payments
    .filter((p) => p.status === PaymentStatus.PAID)
    .reduce((sum, p) => sum + (p.grandTotal ?? 0), 0);

const ensureCanComplete = (
  effectiveTotal: number,
  payments: Payment[],
  isDelayedPaymentGateway?: boolean
) => {
  const isPaymentRequired = effectiveTotal > 0;
  if (!isPaymentRequired) return;
  const paidAmount = getPaymentsPaidTotal(payments ?? []);
  const isFullyPaid = paidAmount >= effectiveTotal;
  const canComplete = isFullyPaid || !!isDelayedPaymentGateway;
  if (!canComplete) {
    throw new Error("Cannot complete order: payment not fully settled");
  }
};

export const updateOrder = async (
  id: string,
  status: OrderStatusType,
  selectedItems?: {
    ticketId?: string;
    entityId?: string;
    quantity: number;
    price?: number;
  }[],
  metaData?: any,
  exhibitorId?: string,
  entitiesFormTemplateModes?: Record<string, "booking" | "request">,
  isDelayedPaymentGateway?: boolean,
  session?: any
) => {
  try {
    const order = await getOrderById(id);
    if (!order) throw new Error(`Order with ID ${id} not found`);
    if (selectedItems?.length) {
      for (const item of selectedItems) {
        if (order?.type === OrderType.ENTITY) {
          if (!item?.entityId) {
            throw new Error("Entity ID is undefined");
          }
          const { valid, error } = await checkEntityBoothAvailability(
            item?.entityId,
            order.id
          );
          if (!valid) {
            throw new Error(error);
          }

          // Reserve the entity
        } else {
          const ticketQuantities: Record<string, number> = {};

          for (const item of selectedItems) {
            if (!item.ticketId) throw new Error("Ticket ID is undefined");

            if (!ticketQuantities[item.ticketId]) {
              ticketQuantities[item.ticketId] = 0;
            }
            ticketQuantities[item.ticketId] += item.quantity;
          }

          for (const ticketId in ticketQuantities) {
            const ticket = await getTicket(ticketId);
            if (!ticket) {
              throw new Error(`Ticket with ID ${ticketId} not found`);
            }
            const { valid, error } = isValidItemQuantity(
              ticketQuantities[ticketId],
              ticket as any
            );
            if (!valid) {
              throw new Error(error);
            }
          }
        }
      }
    }
    if (!order) throw new Error(`Order with ID ${id} not found`);
    const subUnit: number =
      dineroSubUnit({ currency: order?.currency || Currency.SAR }) ?? 1;

    let total = 0;
    let subTotal = 0;
    let tax = 0;
    // Prevent updates to EXPIRED or COMPLETED orders
    if (
      order.status === OrderStatus.EXPIRED // order.status === OrderStatus.COMPLETED
    ) {
      throw new Error(`This order is already ${order.status}`);
    }
    if (
      order?.expiryDate &&
      order.expiryDate < new Date() &&
      order.status !== OrderStatus.COMPLETED
    ) {
      throw new Error(`This order is expired`);
    }
    const event = await getEvent(order.eventId as string, "en", {
      eventMethodConfigs: {
        where: { disabled: false },
        take: 1,
      },
      taxRegion: true,
    });

    let lineItems: LineItem[] = [];
    const updateData: any = { metaData };
    if (selectedItems?.length) {
      for (const item of selectedItems) {
        if (item.ticketId) {
          const ticket = await getTicket(item.ticketId);
          if (!ticket) {
            throw new Error(`Ticket with ID ${item.ticketId} not found`);
          }

          // Calculate total and prepare line items
          const itemTotal = item.quantity * (ticket.price || 0);

          subTotal += createSubtotal({
            subTotal: itemTotal,
            subUnit: subUnit,
            currency: order?.currency,
          });

          tax += calculateTaxBySubtotal({
            subTotal: itemTotal,
            subUnit: subUnit,
            taxRate: order?.taxRate || 1,
            currency: order?.currency,
          });

          total = subTotal + tax;

          if (!event?.eventMethodConfigs?.length && total !== 0)
            throw new Error("Please create and choose payment getaway");
          if (!event?.taxRegion && total !== 0)
            throw new Error("Please create and choose tax");

          updateData.subTotal = subTotal;
          updateData.tax = tax;
          updateData.total = total;
        }
      }

      if (status === OrderStatus.COMPLETED) {
        const effectiveTotal = (updateData.total ?? order.total ?? 0) as number;
        ensureCanComplete(
          effectiveTotal,
          order?.payments ?? [],
          isDelayedPaymentGateway
        );
      }

      const existingTicketIds = order.orderItems.map(
        (item: OrderItem) => item?.ticketId
      );
      const newTicketIds = selectedItems.flatMap((item) =>
        Array(item.quantity).fill(item.ticketId)
      );

      // Determine if selected items have changed
      const isChanged =
        existingTicketIds.length !== newTicketIds.length ||
        existingTicketIds.sort().join(",") !== newTicketIds.sort().join(",");

      const selectedEntityIds = new Set(
        selectedItems
          .map((item) => item.entityId)
          .filter((id): id is string => id !== null)
      );

      // Extract valid entity IDs from existing order items (exclude nulls)
      const previousEntityIds = new Set(
        order?.orderItems
          .map((item) => item.entityId)
          .filter((id): id is string => id !== null)
      );

      // Find entities that were in the order but are NOT in the selected items
      const entitiesToUpdate = [...previousEntityIds].filter(
        (entityId) => !selectedEntityIds.has(entityId)
      );

      if (isChanged || entitiesToUpdate.length > 0) {
        // Delete related form results and existing order items
        await prisma.formResult.deleteMany({
          where: {
            formResultEntity: {
              orderId: id,
            },
          },
        });
        await prisma.orderItem.deleteMany({
          where: { orderId: id },
        });

        // Process new selected items
        const orderItemsPromises = selectedItems.map(async (item) => {
          if (item.entityId) {
            await prisma.entity.updateMany({
              where: {
                id: { in: entitiesToUpdate },
              },
              data: {
                status: EntityStatus.AVAILABLE,
                exhibitorId: null,
                invitationAttendeeId: null,
                sponsorId: null,
              },
            });
            await prisma.orderItem.deleteMany({
              where: { orderId: id },
            });
            if (order.type == OrderType.ENTITY && item.entityId) {
              // Fetch entity with pricing info based on configSource
              const entity = await prisma.entity.findUnique({
                where: { id: item.entityId },
                include: {
                  bookableItem: true,
                  entityTier: {
                    include: {
                      bookableItem: true,
                    },
                  },
                },
              });

              if (!entity) {
                throw new Error(`Entity with ID ${item.entityId} not found`);
              }

              // Calculate entity price based on configSource
              const effectiveBookableItem = getEffectiveBookableItem(
                entity as EntityWithRelations
              );
              const entityPrice = effectiveBookableItem?.price ?? 0;

              lineItems.push({
                name: entity.label,
                description: entity?.label,
                quantity: item.quantity,
                price: entityPrice,
                image: null,
                taxable: null,
                id: entity?.id,
              });
              await prisma.entity.update({
                where: { id: item.entityId },
                data: {
                  status:
                    entitiesFormTemplateModes?.[item.entityId] === "request"
                      ? EntityStatus.ON_HOLD
                      : order.status === OrderStatus.COMPLETED ||
                        status === OrderStatus.COMPLETED
                        ? EntityStatus.RESERVED
                        : EntityStatus.ON_HOLD,
                  ...(exhibitorId && { exhibitorId }),
                },
              });
              return Array.from({ length: item.quantity }, () => ({
                entity: { connect: { id: entity.id } },
                status:
                  item.entityId &&
                    entitiesFormTemplateModes?.[item.entityId] === "request"
                    ? OrderItemStatus.PENDING_APPROVAL
                    : (order.status === OrderStatus.COMPLETED ||
                      status === OrderStatus.COMPLETED) &&
                      !isDelayedPaymentGateway
                      ? OrderItemStatus.CONFIRMED
                      : OrderItemStatus.RESERVED,
              }));
            }
          } else if (item.ticketId) {
            const ticket = await getTicket(item.ticketId);
            if (!ticket) {
              throw new Error(`Ticket with ID ${item.ticketId} not found`);
            }

            const { availableTicket } = (await getTicketQuantity(
              item.ticketId
            )) as { availableTicket: number };

            // Validate available quantity
            if (availableTicket < item.quantity) {
              throw new Error(
                `Not enough available tickets for Ticket ID: ${item.ticketId}`
              );
            }

            // Calculate total and prepare line items
            const itemTotal = item.quantity * (ticket?.price || 0);

            lineItems.push({
              name: ticket.translations?.[0]?.name || "Unnamed Ticket",
              description:
                ticket.translations?.[0]?.description || "No description",
              quantity: item.quantity,
              price: itemTotal * subUnit,
              image: "",
              id: ticket.id,
              taxable: ticket?.taxable,
            });

            return Array.from({ length: item.quantity }, () => ({
              ticket: { connect: { id: ticket.id } },
              status: ticket.requireApproval
                ? OrderItemStatus.PENDING_APPROVAL
                : (order.status === OrderStatus.COMPLETED ||
                  status === OrderStatus.COMPLETED) &&
                  !isDelayedPaymentGateway
                  ? OrderItemStatus.CONFIRMED
                  : OrderItemStatus.RESERVED,
            }));
          }
        });

        const orderItems = (await Promise.all(orderItemsPromises)).flat();
        updateData.orderItems = { create: orderItems };
      } else {
        // If items haven't changed, update the statuses of existing items
        await Promise.all(
          order.orderItems.map(async (item) => {
            item.entityId &&
              (await prisma.entity.update({
                where: { id: item.entityId },
                data: {
                  status:
                    entitiesFormTemplateModes?.[item.entityId] === "request"
                      ? EntityStatus.ON_HOLD
                      : order.status === OrderStatus.COMPLETED ||
                        status === OrderStatus.COMPLETED
                        ? EntityStatus.RESERVED
                        : EntityStatus.ON_HOLD,
                  exhibitorId,
                },
              }));
            if (item.status !== OrderItemStatus.CONFIRMED) {
              await prisma.orderItem.update({
                where: { id: item.id },
                data: {
                  ...(item.entityId && {
                    entity: { connect: { id: item.entityId } },
                  }),
                  ...(item.ticketId && {
                    ticket: { connect: { id: item.ticketId } },
                  }),
                  status:
                    item.entityId &&
                      entitiesFormTemplateModes?.[item.entityId] === "request"
                      ? OrderItemStatus.PENDING_APPROVAL
                      : item?.ticket?.requireApproval
                        ? OrderItemStatus.PENDING_APPROVAL
                        : (order.status === OrderStatus.COMPLETED ||
                          status === OrderStatus.COMPLETED) &&
                          !isDelayedPaymentGateway
                          ? OrderItemStatus.CONFIRMED
                          : OrderItemStatus.RESERVED,
                },
              });
            }
          })
        );

        lineItems = order.orderItems
          .map((item) => {
            if (item.ticket?.id) {
              // Process Ticket Items
              return {
                name: item.ticket.translations?.[0]?.name ?? "Unnamed Ticket",
                description:
                  item.ticket.translations?.[0]?.description ??
                  "No description",
                quantity: 1,
                price: (item.ticket.price ?? 0) * subUnit,
                image: "",
                id: item.ticket.id as string,
                taxable: item.ticket.taxable ?? null,
              };
            } else if (item.entity?.id) {
              // Process Entity Items
              // TODO: Calculate entity price based on configSource
              const entityPrice = 0;
              return {
                name: item.entity.label ?? "Unnamed Entity",
                description: item.entity.label ?? "No description",
                quantity: 1,
                price: entityPrice * subUnit,
                image: "",
                id: item.entity.id as string,
                taxable: null,
              };
            }
            return null;
          })
          .filter((item) => item !== null);
      }
    }

    if (status === OrderStatus.COMPLETED) {
      const effectiveTotal = (updateData.total ?? order.total ?? 0) as number;
      ensureCanComplete(
        effectiveTotal,
        order?.payments ?? [],
        isDelayedPaymentGateway
      );
      updateData.completedAt = new Date();
    }

    // Update the order status if provided
    if (status) updateData.status = status;
    // Add totals and line items to the update data
    updateData.lineItems = lineItems;

    // return order;
    // Update the order in the database
    const updatedOrder = await prisma.order.update({
      where: { id },
      data: updateData,
      include: {
        dtcmOrder: true,
        payments: true,
        formResults: {
          include: {
            formResult: {
              include: {
                formTemplate: true,
              },
            },
          },
        },
        orderItems: {
          include: {
            entity: true,
            formResults: {
              include: {
                formResult: {
                  include: {
                    formTemplate: true,
                  },
                },
              },
            },
          },
        },
      },
    });

    // Create transitions for order and order items after update
    const createdBy = session?.user?.name || "guest";
    const createdById = session?.user?.id || "guest";
    await createOrderTransition({
      status: updatedOrder.status,
      orderId: updatedOrder.id,
      createdBy,
      metaData: {
        createdBy,
        createdById,
      },
    });

    for (const orderItem of updatedOrder.orderItems || []) {
      await createOrderItemTransition({
        status: orderItem.status,
        orderItemId: orderItem.id,
        createdBy,
        metaData: {
          createdBy,
          createdById,
        },
      });
    }

    if (updatedOrder.status === OrderStatus.COMPLETED) {
      warmCheckinRosterEntriesForOrder(updatedOrder.id).catch(() => undefined);
    }

    return updatedOrder;
  } catch (error: any) {
    console.error(`Failed to update order: ${error}`);
    throw new Error(`Failed to update order: ${error.message}`);
  }
};

export const cancelOrder = async (ids: string[]) => {
  try {
    if (!ids.length) throw new Error("Please select order");

    const orders = await prisma.order.findMany({
      where: {
        id: {
          in: ids,
        },
        status: {
          notIn: [OrderStatus.EXPIRED, OrderStatus.CANCELED],
        },
      },
    });

    if (!orders.length) {
      throw new Error("All orders are expired or canceled");
    }

    const updatedOrder = await prisma.order.updateMany({
      where: {
        id: { in: orders.map((order) => order.id) },
      },
      data: { status: "CANCELED" },
    });

    // Cancelling has to give the held booths/seats back to the map
    await releaseOrdersInventory(orders.map((order) => order.id));

    await Promise.allSettled(
      orders.map((order) => invalidateCheckinRosterForOrder(order.id))
    );

    return updatedOrder;
  } catch (error) {
    throw new Error(`Failed to update order: ${error}`);
  }
};

//DELETE
export const deleteOrder = async (id: string) => {
  try {
    const deletedOrder = await prisma.order.update({
      where: {
        id,
      },
      data: {
        status: OrderStatus.DELETED,
        deletedAt: new Date(),
      },
    });
    await invalidateCheckinRosterForOrder(id);
    return deletedOrder;
  } catch (error) {
    throw new Error(`Failed to delete order: ${error}`);
  }
};

export const getFormTemplateByOrderNumber = (
  orderNumber: string,
  eventId: string
) => {
  try {
    const formTemplate = prisma.order.findUnique({
      where: {
        number_eventId: { number: Number(orderNumber), eventId: eventId },
      },
      include: {
        orderItems: {
          include: {
            formResults: {
              include: {
                formResult: {
                  include: {
                    formTemplate: true,
                  },
                },
              },
            },
          },
        },
        formResults: {
          include: {
            formResult: {
              include: {
                formTemplate: true,
              },
            },
          },
        },
      },
    });
    return formTemplate;
  } catch (error) {
    throw new Error(`Failed to get form template by order id: ${error}`);
  }
};

interface QuantityValidationType {
  quantity: number | null;
  minAmount: number | null;
  maxAmount: number | null;
  soldTogether: number | null;
}

export const isValidItemQuantity = (
  requestedQty: number,
  quantityValidation: QuantityValidationType,
  itemType: "ticket" | "entity" = "ticket"
): { valid: boolean; error?: string } => {
  const {
    quantity,
    minAmount = 1,
    maxAmount,
    soldTogether,
  } = quantityValidation;

  // Ensure requested quantity is valid
  if (minAmount && requestedQty < minAmount) {
    return {
      valid: false,
      error: `Minimum ${itemType} purchase is ${minAmount}.`,
    };
  }

  if (maxAmount && requestedQty > maxAmount) {
    return {
      valid: false,
      error: `Maximum ${itemType} purchase is ${maxAmount}.`,
    };
  }

  if (soldTogether && requestedQty % soldTogether !== 0) {
    return {
      valid: false,
      error: `${itemType === "ticket" ? "Tickets" : "Entities"
        } must be purchased in multiples of ${soldTogether}.`,
    };
  }

  if (quantity && requestedQty > quantity) {
    return {
      valid: false,
      error: `Only ${quantity} ${itemType === "ticket" ? "tickets" : "entities"
        } are available.`,
    };
  }

  return { valid: true };
};

export const checkEntityBoothAvailability = async (
  entityId: string,
  orderId?: string
): Promise<{ valid: boolean; error?: string }> => {
  try {
    const entity = await prisma.entity.findUnique({
      where: { id: entityId },
      include: { orderItems: true },
    });
    return {
      valid:
        entity?.status === EntityStatus.AVAILABLE ||
        entity?.orderItems?.some(
          (orderItem) => orderItem.orderId === orderId
        ) ||
        false,
      error:
        entity?.status !== EntityStatus.AVAILABLE
          ? "Entity is not available"
          : undefined,
    };
  } catch (error) {
    throw new Error(`Failed to check entity availability: ${error}`);
  }
};
